<template>
  <div>
    <AlertLayout
      title="Reminders!"
      message="Incorrect information will lead to nurses being unable to request for blood except for theatre."
      type="warning"
    />
    <div class="mx-auto px-10 border border-t-0">
      <h2 class="text-lg font-bold mb-6">Unit screening details</h2>
      <form class="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label class="block mb-1" for="serialNumber"
            >Serial Number <span class="text-red-500">*</span></label
          >
          <input
            type="text"
            v-model="formData.pint_serial"
            class="w-full px-2 py-3 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
            placeholder="Enter Serial Number"
            required
          />
        </div>

        <div>
          <label class="block mb-1" for="bloodGroup"
            >Blood Group <span class="text-red-500">*</span></label
          >
          <select
            v-model="formData.blood_group_id"
            class="w-full px-2 py-3 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
            required
          >
            <option value="" disabled>Select Blood Group</option>
            <option
              v-for="group in bloodGroups"
              :key="group.blood_group_id"
              :value="group.blood_group_id"
            >
              {{ group.description }}
            </option>
          </select>
        </div>

        <div>
          <label class="block mb-1" for="bloodProduct"
            >Blood Product <span class="text-red-500">*</span></label
          >
          <select
            v-model="formData.unit_product_id"
            class="w-full px-2 py-3 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
            required
          >
            <option value="" disabled>Select Blood Product</option>
            <option
              v-for="product in bloodProducts"
              :key="product.unit_product_id"
              :value="product.unit_product_id"
            >
              {{ product.description }}
            </option>
          </select>
        </div>

        <div>
          <label class="block mb-1" for="hepatitisBResult"
            >Hepatitis B Result <span class="text-red-500">*</span></label
          >
          <div class="flex items-center w-full">
            <select
              v-model="formData.hepatitis_b"
              :class="{
                'w-full': !tests.hepatitis_b,
                'w-11/12': tests.hepatitis_b,
              }"
              class="px-2 py-3 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
              required
            >
              <option value="" disabled>Select Result</option>
              <option value="Negative">Negative</option>
              <option value="Positive">Positive</option>
              <option value="Inconclusive">Inconclusive</option>
            </select>
            <div
              v-if="tests.hepatitis_b"
              class="w-1/12 flex items-center justify-center"
            >
              <input
                type="checkbox"
                v-model="confirmedTests['hepatitis_b']"
                :disabled="confirmedTests['hepatitis_b']"
                @change="confirmScreeningTest('hepatitisBResult')"
                class="w-6 h-6 border border-gray-300 rounded bg-gray-50 focus:ring-3 dark:bg-gray-700 dark:border-gray-600 dark:ring-offset-gray-800"
              />
            </div>
          </div>
        </div>

        <div>
          <label class="block mb-1" for="hepatitisCResult"
            >Hepatitis C Result <span class="text-red-500">*</span></label
          >
          <div class="flex items-center w-full">
            <select
              v-model="formData.hepatitis_c"
              :class="{
                'w-full': !tests.hepatitis_c,
                'w-11/12': tests.hepatitis_c,
              }"
              class="px-2 py-3 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
              required
            >
              <option value="" disabled>Select Result</option>
              <option value="Negative">Negative</option>
              <option value="Positive">Positive</option>
              <option value="Inconclusive">Inconclusive</option>
            </select>
            <div
              v-if="tests.hepatitis_c"
              class="w-1/12 flex items-center justify-center"
            >
              <input
                type="checkbox"
                value=""
                v-model="confirmedTests['hepatitis_c']"
                :disabled="confirmedTests['hepatitis_c']"
                @change="confirmScreeningTest('hepatitisCResult')"
                class="w-6 h-6 border border-gray-300 rounded bg-gray-50 focus:ring-3 dark:bg-gray-700 dark:border-gray-600 dark:ring-offset-gray-800"
              />
            </div>
          </div>
        </div>

        <div>
          <label class="block mb-1" for="hivResult"
            >HIV Result <span class="text-red-500">*</span></label
          >
          <div class="flex items-center w-full">
            <select
              v-model="formData.hiv"
              :class="{ 'w-full': !tests.hiv, 'w-11/12': tests.hiv }"
              class="px-2 py-3 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
              required
            >
              <option value="" disabled>Select Result</option>
              <option value="Negative">Negative</option>
              <option value="Positive">Positive</option>
              <option value="Inconclusive">Inconclusive</option>
            </select>
            <div
              v-if="tests.hiv"
              class="w-1/12 flex items-center justify-center"
            >
              <input
                type="checkbox"
                value=""
                v-model="confirmedTests['hiv']"
                :disabled="confirmedTests['hiv']"
                @change="confirmScreeningTest('hivResult')"
                class="w-6 h-6 border border-gray-300 rounded bg-gray-50 focus:ring-3 dark:bg-gray-700 dark:border-gray-600 dark:ring-offset-gray-800"
              />
            </div>
          </div>
        </div>

        <div>
          <label class="block mb-1" for="vdrlResult"
            >VDRL Result <span class="text-red-500">*</span></label
          >
          <div class="flex items-center w-full">
            <select
              v-model="formData.vdrl"
              :class="{ 'w-full': !tests.vdrl, 'w-11/12': tests.vdrl }"
              class="px-2 py-3 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
              required
            >
              <option value="" disabled>Select Result</option>
              <option value="Non-Reactive">Non-Reactive</option>
              <option value="Reactive">Reactive</option>
              <option value="Inconclusive">Inconclusive</option>
            </select>
            <div
              v-if="tests.vdrl"
              class="w-1/12 flex items-center justify-center"
            >
              <input
                type="checkbox"
                value=""
                v-model="confirmedTests['vdrl']"
                :disabled="confirmedTests['vdrl']"
                @change="confirmScreeningTest('vdrlResult')"
                class="w-6 h-6 border border-gray-300 rounded bg-gray-50 focus:ring-3 dark:bg-gray-700 dark:border-gray-600 dark:ring-offset-gray-800"
              />
            </div>
          </div>
        </div>

        <div>
          <label class="block mb-1" for="malariaResult"
            >Malaria Result <span class="text-red-500">*</span></label
          >
          <div class="flex items-center w-full">
            <select
              v-model="formData.malaria"
              :class="{ 'w-full': !tests.malaria, 'w-11/12': tests.malaria }"
              class="px-2 py-3 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
              required
            >
              <option value="" disabled>Select Result</option>
              <option value="Negative">Negative</option>
              <option value="Positive">Positive</option>
              <option value="Inconclusive">Inconclusive</option>
            </select>
            <div
              v-if="tests.malaria"
              class="w-1/12 flex items-center justify-center"
            >
              <input
                type="checkbox"
                value=""
                v-model="confirmedTests['malaria']"
                :disabled="confirmedTests['malaria']"
                @change="confirmScreeningTest('malariaResult')"
                class="w-6 h-6 border border-gray-300 rounded bg-gray-50 focus:ring-3 dark:bg-gray-700 dark:border-gray-600 dark:ring-offset-gray-800"
              />
            </div>
          </div>
        </div>

        <div>
          <label class="block mb-1" for="isClottedResult"
            >Is the blood clotted <span class="text-red-500">*</span></label
          >
          <div class="flex items-center w-full">
            <select
              v-model="formData.blood_clot"
              :class="{
                'w-full': !tests.blood_clot,
                'w-11/12': tests.blood_clot,
              }"
              class="px-2 py-3 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
              required
            >
              <option value="" disabled>Select Answer</option>
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>
            <div
              v-if="tests.blood_clot"
              class="w-1/12 flex items-center justify-center"
            >
              <input
                type="checkbox"
                value=""
                v-model="confirmedTests['blood_clot']"
                :disabled="confirmedTests['blood_clot']"
                @change="confirmScreeningTest('isClottedResult')"
                class="w-6 h-6 border border-gray-300 rounded bg-gray-50 focus:ring-3 dark:bg-gray-700 dark:border-gray-600 dark:ring-offset-gray-800"
              />
            </div>
          </div>
        </div>

        

        <div>
          <label class="block mb-1" for="bloodOrigin"
            >Blood Origin <span class="text-red-500">*</span></label
          >
          <input
            type="text"
             v-model="formData.origin"
            class="w-full px-2 py-3 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
            placeholder="Enter Blood Source"
            required
          />
        </div>

        <div>
          <label class="block mb-1" for="newBloodVolume"
            >New Blood Volume (ml) <span class="text-red-500">*</span></label
          >
          <input
            type="text"
            v-model="formData.volume"
            class="w-full px-2 py-3 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
            placeholder="Enter Blood Volume in millilitres"
            required
          />
        </div>

        <div>
          <label class="block mb-1" for="bloodExpiryDate"
            >Blood Expiry Date <span class="text-red-500">*</span></label
          >
          <input
            type="date"
            v-model="formData.expiry_date"
            class="w-full px-2 py-3 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
            required
          />
        </div>
      </form>

      <div class="flex items-center justify-between mt-5 mb-4">
        <button
          @click="handleBackToBloodUnitsPage"
          class="px-4 py-2 border rounded text-orange-500 border-orange-500"
        >
          Back
        </button>
        <CustomButton
         v-if="!isEditing"
          label="Save"
          bg-class="bg-custom-yellow px-8 py-3"
          @click="handleSubmitNewUnit"
        />
        <CustomButton
        v-else
        label="Update"
        bg-class="bg-custom-blue px-8 py-3"
        @click="handleUpdateScreening"
      />
      </div>
    </div>
  </div>
</template>

<script>
import AlertLayout from "@/components/AlertLayout.vue";
import CustomButton from "@/components/CustomButton.vue";
import api from "@/services/api";
import SweetAlertService from "@/services/SweetAlertService";

export default {
  name: "NewExternalUnitScreening",
  components: {
    AlertLayout,
    CustomButton,
  },
  data() {
    return {
      screeningId: "",
      bloodProducts: [],
      bloodGroups: [],
      formData: {
        pint_serial: "",
        blood_group_id: "",
        unit_product_id: "",
        hepatitis_b: "",
        hepatitis_c: "",
        hiv: "",
        vdrl: "",
        malaria: "",
        origin: "",
        volume: "",
        expiry_date: "",
        blood_clot: "",
        added_by: this.user().user_id,
        facility_id: this.user().facility_id,
        date_added: this.getCurrentDateTime(),
      },
      testApproval: {
        hepatitisBResult: false,
        hepatitisCResult: false,
        hivResult: false,
        vdrlResult: false,
        malariaResult: false,
        isClottedResult: false,
      },
      tests: {
        hepatitis_b: "",
        hepatitis_c: "",
        hiv: "",
        vdrl: "",
        malaria: "",
        blood_clot: "",
      },
      status: "active",
      error: null,
    };
  },
  computed: {
    userData() {
      return this.$store.getters["auth/user"];
    },
    isEditing() {
      return !!this.screeningId; 
    }
  },
  mounted() {
    this.facilityId = this.userData.facility_id;
    this.fetchBloodGroups();
    this.fetchBloodProducts();
    this.screeningId = this.$route.params.external_screening_id;
    if (this.screeningId) {
      this.fetchExternalScreeningData(this.screeningId);
    }
  },

  methods: {
    user() {
      return this.$store.getters["auth/user"];
    },
    handleBackToBloodUnitsPage() {
      this.$router.push({ name: "InventoryManagementPage" });
    },
    async fetchBloodGroups() {
      try {
        const response = await api.getBloodGroups();
        this.bloodGroups = response || [];
      } catch (error) {
        console.error("Error fetching blood groups:", error);
      }
    },
    async fetchBloodProducts() {
      try {
        const response = await api.getUnitProducts();
        this.bloodProducts = response || [];
      } catch (error) {
        this.error = "Failed to fetch blood products";
      }
    },

    validateFields() {
      const emptyFields = [];
      if (!this.formData.pint_serial) emptyFields.push("Unit Serial");
      if (!this.formData.origin) emptyFields.push("Unit Origin");
      if (!this.formData.blood_group_id) emptyFields.push("Blood Group");
      if (!this.formData.unit_product_id) emptyFields.push("Blood Product");
      if (!this.formData.volume) emptyFields.push("Volume");
      if (!this.formData.expiry_date) emptyFields.push("Expiry Date");

      return emptyFields;
    },
    async handleSubmitNewUnit() {
      const data = {
        pint_serial: this.formData.pint_serial,
        origin: this.formData.origin,
        blood_group_id: this.formData.blood_group_id,
        unit_product_id: this.formData.unit_product_id,
        volume: this.formData.volume,
        expiry_date: this.formData.expiry_date,
        hepatitis_b: this.formData.hepatitis_b,
        hepatitis_c: this.formData.hepatitis_c,
        hiv: this.formData.hiv,
        vdrl: this.formData.vdrl,
        malaria: this.formData.malaria,
        blood_clot: this.formData.blood_clot,
        added_by: this.formData.added_by,
        facility_id: this.formData.facility_id,
        date_added: this.formData.date_added,
      };

      const emptyFields = this.validateFields();
      if (emptyFields.length > 0) {
        SweetAlertService.showEmptyFieldsAlert(emptyFields);
        return;
      }

      try {
        await api.createExternalScreening(data);
        await SweetAlertService.showSuccess(
          "Success!",
          "Blood Product Added successfully!."
        );
        this.$router.push({ name: "InventoryManagementPage" });
      } catch (error) {
        this.error = error.message || "Failed to save Blood Product";
        console.error("Error:", this.error);
        SweetAlertService.showError("Error", "Error updating Blood Product.");
      }
    },

    getCurrentDateTime() {
      const now = new Date();
      const year = now.getFullYear();
      const month = ("0" + (now.getMonth() + 1)).slice(-2);
      const day = ("0" + now.getDate()).slice(-2);
      const hours = ("0" + now.getHours()).slice(-2);
      const minutes = ("0" + now.getMinutes()).slice(-2);
      const seconds = ("0" + now.getSeconds()).slice(-2);
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    },

    async fetchExternalScreeningData(screeningId) {
  try {
    const response = await api.getExternalScreening(screeningId);

    if (response) {
      this.formData = {
        pint_serial: response.pint_serial || "",
        blood_group_id: response.blood_group_id || "",
        unit_product_id: response.unit_product_id || "",
        hepatitis_b: response.hepatitis_b || "",
        hepatitis_c: response.hepatitis_c || "",
        hiv: response.hiv || "",
        vdrl: response.vdrl || "",
        malaria: response.malaria || "",
        blood_clot: response.blood_clot || "",
        origin: response.origin || "",
        volume: response.volume || "",
        expiry_date: response.expiry_date || "",
        added_by: this.user().user_id,
        facility_id: this.user().facility_id,
        date_added: this.getCurrentDateTime(),
      };

      // Mapping frontend test names to backend property names
      const backendPropertyMapping = {
        hepatitis_b: "hepatitis_b",
        hepatitis_c: "hepatitis_c",
        hiv: "hiv",
        vdrl: "vdrl",
        malaria: "malaria",
        blood_clot: "blood_clot",
      };

      // Track which tests have been confirmed
      this.confirmedTests = {};

      response.external_screening_logs.forEach((log) => {
        if (log.confirmed_by && log.confirmed_by.user_id) {
          this.confirmedTests[log.property] = true; // Mark as confirmed
        }
      });

      // Enable checkboxes only for tests that haven't been confirmed
      this.tests = Object.keys(backendPropertyMapping).reduce((acc, key) => {
        acc[key] = response[key] ? true : false;
        return acc;
      }, {});

      // Set approval state, preventing already confirmed tests from being changed
      this.testApproval = Object.keys(backendPropertyMapping).reduce(
        (acc, key) => {
          acc[key + "Result"] = !this.confirmedTests[backendPropertyMapping[key]] ;
          return acc;
        },
        {}
      );
    }
  } catch (error) {
    console.error("Error fetching external screening data:", error);
  }
},

    async confirmScreeningTest(testProperty) {
    try {
      const backendPropertyMapping = {
        hepatitisBResult: "hepatitis_b",
        hepatitisCResult: "hepatitis_c",
        hivResult: "hiv",
        vdrlResult: "vdrl",
        malariaResult: "malaria",
        isClottedResult: "blood_clot",
      };

      // Get the backend key
      const backendKey = backendPropertyMapping[testProperty];

      // Check if the value is defined
      if (!backendKey || this.formData[backendKey] === undefined || this.formData[backendKey] === "") {
        console.error(`Error: No value found for ${testProperty}`);
        return; // Prevent sending an empty request
      }

      // Construct the payload
      const payload = {
        external_screening: {
          [backendKey]: this.formData[backendKey],
        },
      };

      // Make the API call
      await api.confirmExternalScreeningTest(this.screeningId, payload);
    } catch (error) {
      console.error("Error confirming screening test:", error);
      this.error = "Failed to confirm screening test.";
    }
  },

  async handleUpdateScreening() {
      try {
        // Only send updated fields to the backend
        const updatedFields = Object.keys(this.formData).reduce((acc, key) => {
          if (this.formData[key] !== "" && this.formData[key] !== null) {
            acc[key] = this.formData[key];
          }
          return acc;
        }, {});

        if (Object.keys(updatedFields).length === 0) {
          console.warn("No changes detected.");
          return;
        }

        await api.updateExternalScreeningTest(this.screeningId, updatedFields);
        await SweetAlertService.showSuccess("Success!", "Screening updated successfully!");
        this.$router.push({ name: "InventoryManagementPage" });
      } catch (error) {
        console.error("Error updating screening:", error);
        SweetAlertService.showError("Error", "Failed to update screening.");
      }
    },

  },
};
</script>

<style lang="css" scoped></style>
